import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ADMIN_MENU } from "../config/menu";
import { useApp } from "../context/AppContext";
import { useCart } from "../context/CartContext";
import { useI18n } from "../i18n";
import { Avatar, Button } from "../components/ui";
import Icon from "../components/ui/Icon";

export default function AdminLayout() {
  const { organization, organizations, switchOrg, branches, branchId, switchBranch, enabledModules, profile, logout, businessModel, planActive, plan, subscription, moduleLocked } = useApp();
  const { count: cartItems } = useCart();
  const { t, tx, lang, setLang, pick } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [orgMenu, setOrgMenu] = useState(false);
  const [userMenu, setUserMenu] = useState(false);

  useEffect(() => {
    setOpen(false);
    setOrgMenu(false);
    setUserMenu(false);
  }, [location.pathname]);

  // เมนูที่ใช้ไม่ได้จะไม่ซ่อน แต่โชว์กุญแจ กดเข้าไปได้ หน้านั้นบอกว่าต้องซื้อ/อัปเกรดแพ็กเกจ
  //   - ยังไม่ซื้อ / หมดอายุ  -> ใช้ชุดฟรี เมนูนอกชุดฟรีล็อก
  //   - โมดูลไม่อยู่ในแพ็กเกจ -> ล็อกเฉพาะเมนูนั้น (ชวนอัปเกรด)
  //   - เจ้าของร้านปิดโมดูลเอง -> ซ่อนตามเดิม
  const groups = useMemo(() => {
    const decorate = (item) => {
      if (item.always) return { ...item, locked: false };
      if (item.module && moduleLocked(item.module)) return { ...item, locked: true };
      if (item.module && !enabledModules.includes(item.module)) return null;
      return { ...item, locked: false };
    };

    return ADMIN_MENU.map((group) => ({ ...group, items: group.items.map(decorate).filter(Boolean) })).filter(
      (group) => group.items.length > 0
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledModules, planActive]);

  const title = useMemo(() => {
    if (location.pathname.startsWith("/admin/cart")) return tx("ตะกร้า", "Cart");
    // หาชื่อหน้าจากเมนูทั้งหมด (รวมโมดูลที่ถูกปิดอยู่ด้วย) เผื่อเข้าหน้าตรงผ่าน URL
    const all = ADMIN_MENU.flatMap((group) => group.items);
    const found = all
      .filter((item) => (item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to)))
      .sort((a, b) => b.to.length - a.to.length)[0];
    return found ? pick(found, "label") : t("app.name");
  }, [location.pathname, pick, t, tx]);

  return (
    <div className="crm-shell">
      <aside className={`crm-sidebar ${open ? "is-open" : ""}`}>
        <div className="crm-sidebar__brand">
          <span className="crm-logo">LU</span>
          <div>
            <div>LevelUpCRM</div>
            <div style={{ fontSize: 11, color: "#a5b4fc", fontWeight: 500 }}>{t("app.tagline")}</div>
          </div>
        </div>

        <div className="crm-sidebar__org" style={{ position: "relative" }}>
          {/* แต่ละบัญชีผูกกับร้านเดียว — ปุ่มสลับร้านโผล่เฉพาะบัญชีที่มีหลายร้าน */}
          <button
            type="button"
            className="crm-org-switch"
            onClick={() => organizations.length > 1 && setOrgMenu((v) => !v)}
          >
            <Icon name={businessModel.icon} /> {organization?.name || "-"}
            <small>{pick(businessModel)}{organizations.length > 1 ? ` · ${t("nav.switchOrg")}` : ""}</small>
          </button>
          {orgMenu && organizations.length > 1 ? (
            <div
              style={{ position: "absolute", top: "100%", left: 12, right: 12, background: "#fff", borderRadius: 10, boxShadow: "0 18px 40px rgba(15,23,42,.3)", zIndex: 40, padding: 6 }}
            >
              {organizations.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => switchOrg(org.id)}
                  style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: org.id === organization?.id ? "#eef2ff" : "none", padding: "9px 10px", borderRadius: 8, cursor: "pointer", fontSize: 13 }}
                >
                  {org.name}
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* สถานะแพ็กเกจ — เตือนล่วงหน้า 7 วันก่อนหมดอายุ */}
        <Link to="/admin/subscription" className={`crm-plan-chip ${planActive ? "" : "is-warn"}`}>
          <span><Icon name={planActive ? "badge" : "lock"} /></span>
          <span style={{ minWidth: 0 }}>
            <b>{planActive ? pick(plan) : tx("ใช้งานฟรี (จำกัด)", "Free (limited)")}</b>
            <small>
              {planActive
                ? tx(`เหลืออีก ${subscription.daysLeft} วัน`, `${subscription.daysLeft} days left`)
                : tx("ซื้อแพ็กเกจเพื่อปลดล็อกทุกเมนู", "Buy a plan to unlock all menus")}
            </small>
          </span>
        </Link>

        <div className="crm-sidebar__scroll">
          {groups.map((group) => (
            <div key={group.group}>
              <div className="crm-sidebar__group">{pick(group, "group")}</div>
              {group.items.map((item) =>
                item.locked ? (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    className={({ isActive }) => `crm-nav-link is-locked ${isActive ? "is-active" : ""}`}
                    title={tx("ต้องซื้อ/อัปเกรดแพ็กเกจก่อนใช้งาน", "Buy or upgrade your plan to unlock")}
                  >
                    <span className="crm-nav-link__icon"><Icon name={item.icon} /></span>
                    {pick(item, "label")}
                    <span className="crm-nav-link__lock"><Icon name="lock" /></span>
                  </NavLink>
                ) : (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    className={({ isActive }) => `crm-nav-link ${isActive ? "is-active" : ""}`}
                  >
                    <span className="crm-nav-link__icon"><Icon name={item.icon} /></span>
                    {pick(item, "label")}
                  </NavLink>
                )
              )}
            </div>
          ))}
        </div>
      </aside>

      {open ? <div className="crm-backdrop" onClick={() => setOpen(false)} /> : null}

      <div className="crm-main">
        <header className="crm-topbar">
          <Button className="crm-burger" size="sm" onClick={() => setOpen((v) => !v)}>
            <Icon name="menu" />
          </Button>
          <h2 className="crm-topbar__title">{title}</h2>

          <div className="crm-topbar__spacer" />

          <select
            className="select-crm"
            style={{ width: "auto", minWidth: 150 }}
            value={branchId}
            onChange={(event) => switchBranch(event.target.value)}
          >
            <option value="">ทุกสาขา</option>
            {branches.map((item) => (
              <option key={item.id} value={item.id}>
                {pick(item)}
              </option>
            ))}
          </select>

          <Link
            to="/admin/cart"
            className="btn-crm btn-ghost-crm btn-sm-crm"
            style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}
            title={tx("ตะกร้า", "Cart")}
            aria-label={tx(`ตะกร้า ${cartItems} รายการ`, `Cart, ${cartItems} items`)}
          >
            <Icon name="cart" />
            {cartItems > 0 ? (
              <span style={{ minWidth: 18, height: 18, padding: "0 5px", borderRadius: 9, background: "var(--c-rose, #f43f5e)", color: "#fff", fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                {cartItems > 99 ? "99+" : cartItems}
              </span>
            ) : null}
          </Link>

          <Button size="sm" onClick={() => setLang(lang === "th" ? "en" : "th")}>
            {lang === "th" ? "🇹🇭 ไทย" : "🇬🇧 EN"}
          </Button>

          <div style={{ position: "relative" }}>
            <button type="button" style={{ border: "none", background: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }} onClick={() => setUserMenu((v) => !v)}>
              <Avatar name={profile?.fullname || profile?.email} size="sm" />
            </button>
            {userMenu ? (
              <div style={{ position: "absolute", right: 0, top: 42, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, boxShadow: "0 18px 40px rgba(15,23,42,.16)", zIndex: 50, minWidth: 200, padding: 6 }}>
                <div style={{ padding: "8px 10px", fontSize: 13 }}>
                  <div className="u-strong">{profile?.fullname || profile?.email}</div>
                  <div className="u-fs-12 u-muted">{profile?.email}</div>
                </div>
                <Link to="/admin/organization" style={{ display: "block", padding: "8px 10px", fontSize: 13, color: "#334155" }}>
                  {t("nav.settings")}
                </Link>
                <button
                  type="button"
                  style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "none", padding: "8px 10px", color: "#f43f5e", cursor: "pointer", fontSize: 13 }}
                  onClick={async () => {
                    await logout();
                    navigate("/auth/login");
                  }}
                >
                  {t("action.logout")}
                </button>
              </div>
            ) : null}
          </div>
        </header>

        <main className="crm-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
